import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import cornerstone from 'cornerstone-core';
import dicomParser from 'dicom-parser';
import cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader';
import { Save, Upload } from 'lucide-react';
import ProgressBar from './ProgressBar';
import ImageCardList from './ImageCardList';
import DicomViewer from './DicomViewer';
import ImagingHeader from './ImagingHeader';
import ImagingEmptyState from './ImagingEmptyState';
import StudyListControls, { StudyListNoResults } from './StudyListControls';
import { useStudyListControls } from './useStudyListControls';
import { dicomHelpers } from '../services/dicomHelpers';
import { caseExportSynthesizer } from '../services/caseExportSynthesizer';
import { api } from '../services/api';

cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
cornerstoneWADOImageLoader.external.dicomParser = dicomParser;

// Maximum number of in-flight upload requests during a single save. Tuned to
// trade off browser concurrency limits and server load.
const UPLOAD_CONCURRENCY = 5;

const IMAGES_TAB_ATTENTION_MS = 4500;

// runWithConcurrency drives `worker(item, index)` across `items` with at most
// `limit` promises in flight at once. Worker results are collected into an
// array positionally matching `items`. If any worker throws, the first error
// is captured, no new items are dispatched, and the helper rejects once the
// currently in-flight workers settle.
async function runWithConcurrency(items, limit, worker) {
    const results = new Array(items.length);
    let nextIndex = 0;
    let firstError = null;

    async function pump() {
        while (firstError === null) {
            const i = nextIndex;
            nextIndex += 1;
            if (i >= items.length) return;
            try {
                results[i] = await worker(items[i], i);
            } catch (err) {
                if (firstError === null) firstError = err;
                return;
            }
        }
    }

    const poolSize = Math.min(limit, items.length);
    await Promise.all(Array.from({ length: poolSize }, pump));
    if (firstError) throw firstError;
    return results;
}

function UploadImages({ toast, user, onLogin, onUploadLogin }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(false);
    const [saveStatus, setSaveStatus] = useState({ state: 'idle', message: '' });
    const [currentStudyUID, setCurrentStudyUID] = useState('');
    const fileInputRef = useRef(null);
    // Guards the `?action=upload` auto-open against React's strict-mode
    // double-invocation in dev and against any future re-runs of the
    // effect — once we've fired the picker for a given mount, we're
    // done. We also strip the query param itself, so refresh/back nav
    // won't re-trigger the picker either.
    const autoUploadFiredRef = useRef(false);
    const [studies, setStudies] = useState([]);
    const [currentSeriesKey, setCurrentSeriesKey] = useState('');
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isViewerOpen, setIsViewerOpen] = useState(false);
    const [progressState, setProgressState] = useState({
        visible: false,
        fading: false,
        label: '',
        percent: 0,
        detail: ''
    });

    const intervalRef = useRef(null);
    const hasNotifiedRef = useRef(false);
    const imageListRef = useRef(null);
    // Serializes every generateStudyThumbnails run kicked off by
    // loadDicomFiles. handleSave awaits this before reading thumbnailBlob /
    // thumbnailHash so a save can never outrun generation; chaining (rather
    // than replacing) keeps repeated folder loads ordered, and the catch
    // stops one failed study from poisoning the chain for later saves.
    const thumbnailWorkRef = useRef(Promise.resolve());
    // Tracks the previous value of `isSaveDisabled` across renders so we
    // can detect the disabled -> enabled transition (i.e. the moment the
    // first study finishes loading) and briefly draw the user's eye to
    // the now-actionable Save button.
    const prevSaveDisabledRef = useRef(true);
    const [saveAttention, setSaveAttention] = useState(false);
    // existingStudyUIDsPromiseRef resolves to a Set<string> of
    // studyInstanceUIDs that are already saved in the cloud for the
    // current user. We kick the fetch off on mount but don't block the
    // UI on it — the load flow awaits this promise at the end (right
    // before deduping) so the user can pick a folder while the network
    // request is still in flight.
    const existingStudyUIDsPromiseRef = useRef(null);
    const [savedStudiesCount, setSavedStudiesCount] = useState(null);
    const [sharesCount, setSharesCount] = useState(null);
    const [imagesTabAttention, setImagesTabAttention] = useState({ active: false, token: 0 });
    const imagesTabAttentionTimeoutRef = useRef(null);
    const listControls = useStudyListControls(studies);

    const triggerImagesTabAttention = () => {
        if (imagesTabAttentionTimeoutRef.current) clearTimeout(imagesTabAttentionTimeoutRef.current);
        setImagesTabAttention((prev) => ({ active: true, token: prev.token + 1 }));
        imagesTabAttentionTimeoutRef.current = setTimeout(() => {
            imagesTabAttentionTimeoutRef.current = null;
            setImagesTabAttention((prev) => ({ ...prev, active: false }));
        }, IMAGES_TAB_ATTENTION_MS);
    };

    useEffect(() => {
        return () => {
            if (imagesTabAttentionTimeoutRef.current) clearTimeout(imagesTabAttentionTimeoutRef.current);
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        setSavedStudiesCount(null);
        setSharesCount(null);
        existingStudyUIDsPromiseRef.current = (async () => {
            if (!user) return new Set();
            try {
                const response = await api.getImagingFolders();
                if (cancelled) return new Set();
                const saved = response || [];
                setSavedStudiesCount(saved.length);
                return new Set(
                    saved
                        .map((s) => s.studyInstanceUID)
                        .filter(Boolean)
                );
            } catch (err) {
                console.log('Failed to load existing studies:', err);
                return new Set();
            }
        })();
        if (user) {
            api.getImagingShares()
                .then((response) => {
                    if (!cancelled) setSharesCount(Array.isArray(response) ? response.length : 0);
                })
                .catch((err) => {
                    console.log('Failed to load shares count:', err);
                });
        }
        return () => { cancelled = true; };
    }, [user]);

    // TODO: REVISIT — this effect is dead code and never fires.
    //
    // Studies built by extractStudiesFromImageData have no top-level
    // `progress` field; save progress is written to `loading.progress` by
    // setStudyLoading. So `s.progress?.percent ?? 0` is always 0, allDone is
    // always false, and the 'All studies saved.' toast never appears.
    //
    // intervalRef is part of the same dead path: it is allocated and cleared
    // in two places but never assigned, so both clearInterval calls are
    // no-ops. Either point this at loading.progress and keep the interval,
    // or delete the effect, the ref and the cleanup effect below outright.
    useEffect(() => {
        if (!studies.length) return;
        const allDone = studies.every(s => (s.progress?.percent ?? 0) >= 100);
        if (allDone && !hasNotifiedRef.current) {
            hasNotifiedRef.current = true;
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            toast('All studies saved.');
        }
    }, [studies, toast]);

    // Clean up the interval if the component unmounts mid-save.
    useEffect(() => {
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, []);

    // ImagesPage's empty-state action navigates here with
    // `?action=upload` so a single click can take the user from "no
    // images" straight into the OS file picker. Open the picker once,
    // then drop the query param so refresh / back-navigation don't
    // re-open it. The ref guard makes this safe under React strict
    // mode's double-effect invocation in dev.
    useEffect(() => {
        if (searchParams.get('action') !== 'upload') return;
        if (autoUploadFiredRef.current) return;
        autoUploadFiredRef.current = true;
        if (!user) {
            onUploadLogin();
            return;
        }
        fileInputRef.current?.click();
        const next = new URLSearchParams(searchParams);
        next.delete('action');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams, user, onUploadLogin]);


    const handleSeriesClick = (studyKey, seriesKey, sliceIndex) => {
        setCurrentStudyUID(studyKey);
        setCurrentSeriesKey(seriesKey);
        setCurrentIndex(sliceIndex || 0);
        setIsViewerOpen(true);
    };

    // TODO: REVISIT — unreachable. DicomViewer dropped its onStudyChange /
    // onSeriesChange props along with the study/series dropdowns, so neither
    // this nor handleViewerSeriesChange can be called. Note the latent bug
    // before re-enabling: firstSeries?.seriesKey below is always undefined
    // because series objects use seriesInstanceUID. See the note at the top
    // of DicomViewer.js.
    const handleViewerStudyChange = (newStudyUID) => {
        setCurrentStudyUID(newStudyUID);
        const newStudy = studies.find(s => s.studyInstanceUID === newStudyUID);
        const firstSeries = newStudy?.series?.find(se => {
            const slices = Array.isArray(se.slices) ? se.slices
                : Array.isArray(se.images) ? se.images
                    : Array.isArray(se) ? se
                        : [];
            return slices.length > 0;
        });
        setCurrentSeriesKey(firstSeries?.seriesKey || '');
        setCurrentIndex(0);
    };

    const handleViewerSeriesChange = (newSeriesKey) => {
        setCurrentSeriesKey(newSeriesKey);
        setCurrentIndex(0);
    };

    const handleSelectFolderClick = () => {
        if (!user) {
            onUploadLogin();
            return;
        }
        if (!fileInputRef.current) return;
        fileInputRef.current.click();
    };

    // setStudyLoading replaces the per-study `loading` field on the study with
    // matching studyInstanceUID. ImageCard reads `image.loading.progress` to
    // render its own per-card progress bar, so this is how save-time progress
    // is surfaced to the user (the global ProgressBar above the list is
    // reserved for the disk-load phase).
    const setStudyLoading = (studyKey, loadingValue) => {
        setStudies((prev) => prev.map((s) => (
            s.studyInstanceUID === studyKey
                ? { ...s, loading: loadingValue }
                : s
        )));
    };

    const handleLocalTagsChange = (image, nextTags) => {
        setStudies((prev) => prev.map((s) => {
            if (s.studyInstanceUID !== image.studyInstanceUID) return s;
            s.tags = nextTags;
            return s;
        }));
    };

    // TODO: REVISIT — the save flow needs a full audit and bug-fix pass.
    // Known issues to work through when that happens:
    //
    //   1. A series whose middle slice fails to render still persists with
    //      an empty thumbnailUrl (renderSliceToBlob returns null and
    //      generateStudyThumbnails swallows it), and nothing ever
    //      regenerates it, so it shows a permanent 'Loading…' placeholder in
    //      SeriesHover. Needs a retry or an on-demand regeneration path.
    //
    //   2. generateStudyThumbnails mutates the study objects in place rather
    //      than going through setStudies, so React is never told the
    //      thumbnails arrived and the list may not repaint. Do not fix this
    //      in isolation: the loop below iterates the `studies` array captured
    //      at click time, and that in-place mutation is what lets thumbnails
    //      finishing mid-save still reach the studies later in the loop.
    //      Switching to immutable updates without also reading studies from a
    //      ref here reintroduces the empty-thumbnailUrl bug for every study
    //      after the first.
    //
    //   3. Partial-failure recovery. runWithConcurrency stops dispatching on
    //      the first error but lets in-flight uploads finish, so a failed
    //      save can leave files in S3 and ent_file rows behind with no study
    //      referencing them. See the orphan-GC note in
    //      handlers/imaging.go UploadImagingFile. There is no resume or
    //      retry — the user has to start the whole study over.
    //
    //   4. saveStatus.message is populated on error but never rendered; the
    //      only feedback is a generic toast.
    //
    // handleSave uploads a study in three sequential phases so that a single
    // running progress percentage can be surfaced on the study's card:
    //   1. Upload each unique source file via POST /api/imaging/file
    //   2. Upload each series thumbnail via POST /api/imaging/thumbnail
    //   3. Submit the remaining metadata via POST /api/imaging/metadata
    // Within phases 1 and 2 requests are fanned out with at most
    // UPLOAD_CONCURRENCY in flight at any time. Total work units = files +
    // thumbnails + 1 (the metadata request); progress advances one unit per
    // completed request regardless of dispatch order.
    const handleSave = async () => {
        if (!studies.length) return;
        if (!user) {
            onLogin();
            return;
        }
        hasNotifiedRef.current = false;
        setSaveStatus({ state: 'saving', message: '' });

        let activeStudyKey = null;
        try {
            await thumbnailWorkRef.current;

            for (const study of studies) {
                const studyKey = study.studyInstanceUID;
                activeStudyKey = studyKey;

                // Dedupe by sourceFileHash so multi-frame slices that share a
                // single underlying File only upload once.
                const uniqueByHash = new Map();
                study.series.forEach((seriesEntry) => {
                    seriesEntry.slices.forEach((slice) => {
                        if (slice.sourceFile && slice.sourceFileHash && !uniqueByHash.has(slice.sourceFileHash)) {
                            uniqueByHash.set(slice.sourceFileHash, slice.sourceFile);
                        }
                    });
                });
                const filesToUpload = Array.from(uniqueByHash.entries()).map(([hash, file]) => ({ file, hash }));

                const thumbnailsToUpload = study.series.filter(
                    (s) => s.thumbnailBlob && s.thumbnailHash && s.seriesInstanceUID
                );

                const totalSteps = filesToUpload.length + thumbnailsToUpload.length + 1;
                let completedSteps = 0;
                const tickProgress = (detail) => {
                    completedSteps += 1;
                    setStudyLoading(studyKey, {
                        type: 'progress',
                        progress: {
                            label: 'Saving study',
                            percent: (completedSteps / totalSteps) * 100,
                            detail,
                        },
                    });
                };

                setStudyLoading(studyKey, {
                    type: 'progress',
                    progress: {
                        label: 'Saving study',
                        percent: 0,
                        detail: `Uploading 0/${filesToUpload.length} file(s)`,
                    },
                });

                // Counters used by the progress detail string. Worker
                // completions race, so we display the count of finished items
                // (atomic increment under JS's single-threaded event loop)
                // rather than the index of the item that just resolved.
                let filesDone = 0;
                await runWithConcurrency(filesToUpload, UPLOAD_CONCURRENCY, async ({ file, hash }) => {
                    await api.uploadImagingFile(file, hash);
                    filesDone += 1;
                    tickProgress(`Uploaded ${filesDone}/${filesToUpload.length} file(s)`);
                });

                const thumbnailUrlBySeriesKey = {};
                let thumbnailsDone = 0;
                await runWithConcurrency(thumbnailsToUpload, UPLOAD_CONCURRENCY, async (seriesEntry) => {
                    const filename = `${seriesEntry.thumbnailHash}.jpg`;
                    const result = await api.uploadImagingThumbnail(seriesEntry.thumbnailBlob, filename);
                    if (result?.url) {
                        thumbnailUrlBySeriesKey[dicomHelpers.getSeriesKey(seriesEntry)] = result.url;
                    }
                    thumbnailsDone += 1;
                    tickProgress(`Uploaded ${thumbnailsDone}/${thumbnailsToUpload.length} thumbnail(s)`);
                });

                const metadata = dicomHelpers.buildSaveMetadata(study, thumbnailUrlBySeriesKey);
                await api.saveImagingMetadata(metadata);
                tickProgress('Saved metadata');

                activeStudyKey = null;

                // Track the saved UID so subsequent disk loads in this
                // session dedupe against it as well. The Set lives behind
                // the promise ref; await it (it's already resolved by the
                // time we get here) and mutate in place.
                if (studyKey && existingStudyUIDsPromiseRef.current) {
                    const existing = await existingStudyUIDsPromiseRef.current;
                    existing.add(studyKey);
                }

                // Kick off the "pop off the stack" collapse animation and
                // wait for it to finish before moving on to the next
                // study. ImageCardList owns the animation timing and
                // invokes our onStudyRemoved callback below to drop the
                // study from state once the transition has played out.
                // Subsequent items slide up automatically because the
                // parent flex container relayouts as this row's height
                // shrinks.
                toast('Study saved.');
                if (imageListRef.current) {
                    await imageListRef.current.removeStudy(studyKey);
                } else {
                    setStudies((prev) => prev.filter((s) => s.studyInstanceUID !== studyKey));
                }

                setSavedStudiesCount((prev) => (Number.isFinite(prev) ? prev + 1 : prev));
                triggerImagesTabAttention();
            }

            setSaveStatus({ state: 'saved', message: '' });
        } catch (err) {
            console.log('handleSave error:', err);
            setSaveStatus({ state: 'error', message: err?.message || 'Failed to save' });
            if (activeStudyKey) {
                setStudyLoading(activeStudyKey, null);
            }
            if (err.response?.status === 401) {
                onLogin();
            } else {
                toast('Failed to save study.');
            }
        }
    };

    const handleFilesChosen = async (event) => {
        const files = Array.from(event.target.files || []);
        if (!files.length) return;
        await loadDicomFiles(files);
        event.target.value = '';
    };

    const loadDicomFiles = async (selectedFiles) => {
        setIsLoading(true);
        setProgressState({ fading: false, visible: true, label: 'Loading image from disk', percent: 0, detail: 'Starting...' });
        const files = await caseExportSynthesizer.expandCaseExports(selectedFiles, (done, total) => {
            setProgressState((prev) => ({
                ...prev,
                label: 'Converting case export images',
                percent: (done / total) * 100,
                detail: `${done}/${total} image(s) converted`,
            }));
        });
        setProgressState((prev) => ({ ...prev, label: 'Loading image from disk', percent: 0, detail: 'Starting...' }));
        const successfullyLoadedFiles = [];
        const totalFiles = files.length || 1;
        let processedFiles = 0;
        const imageData = [];
        for (const file of files) {
            try {

                let newImages = await dicomHelpers.extractImageDataFromFile(file);
                for (let i = 0; i < newImages.length; i++) {
                    imageData.push(newImages[i]);
                }
                successfullyLoadedFiles.push(file);
            } catch (fileError) {
                console.log("fileError: ", fileError);
            } finally {
                processedFiles += 1;
                setProgressState((prev) => ({ ...prev, percent: (processedFiles / totalFiles) * 100, detail: `${processedFiles}/${totalFiles} file(s) processed` }));
            }
        }

        if (!imageData.length) {
            console.log("NO DICOM FILES FOUND");
            setIsLoading(false);
            setProgressState((prev) => ({ ...prev, fading: true }));
            toast('No DICOM files found in the selected folder.', 'warning');
            return;
        }

        const grouped = dicomHelpers.extractStudiesFromImageData(imageData);

        // Block here (rather than at the load button) until we know which
        // studies already exist in the cloud, then drop any duplicates.
        // The promise was kicked off on mount, so in the common case this
        // is already resolved by the time disk parsing finishes.
        setProgressState((prev) => ({
            ...prev,
            detail: 'Checking saved studies...'
        }));
        const savedStudyUIDs = (await existingStudyUIDsPromiseRef.current) || new Set();
        // Also dedupe against studies that are already in the in-memory
        // list but haven't been saved to the cloud yet. Without this, a
        // user who loads the same folder twice (or two folders that share
        // a study) would see the same study appear twice in the list.
        const loadedStudyUIDs = new Set(
            studies.map((s) => s.studyInstanceUID).filter(Boolean)
        );
        const newStudies = [];
        const savedDuplicates = [];
        const loadedDuplicates = [];
        for (const study of grouped) {
            const uid = study.studyInstanceUID;
            if (uid && savedStudyUIDs.has(uid)) {
                savedDuplicates.push(study);
            } else if (uid && loadedStudyUIDs.has(uid)) {
                loadedDuplicates.push(study);
            } else {
                newStudies.push(study);
            }
        }

        thumbnailWorkRef.current = thumbnailWorkRef.current
            .then(() => dicomHelpers.generateStudyThumbnails(newStudies, () => { }))
            .catch((err) => { console.log('generateStudyThumbnails error:', err); });

        setIsLoading(false);
        console.log("studies are: ", newStudies);
        // Append rather than replace so a load doesn't wipe out studies
        // the user already pulled in from an earlier folder selection.
        // The functional updater also makes the dedupe race-safe if any
        // study finished saving (and was removed) between the closure
        // capture above and this state write.
        setStudies((prev) => {
            const prevUIDs = new Set(prev.map((s) => s.studyInstanceUID).filter(Boolean));
            return [...prev, ...newStudies.filter((s) => !s.studyInstanceUID || !prevUIDs.has(s.studyInstanceUID))];
        });

        setProgressState((prev) => ({ ...prev, percent: 100, detail: 'Complete' }));

        setTimeout(() => {
            setProgressState((prev) => ({ ...prev, fading: true }));
        }, 400);

        const messages = [];
        if (newStudies.length) {
            const noun = newStudies.length === 1 ? 'study' : 'studies';
            messages.push(`${newStudies.length} ${noun} loaded. Make sure to save before leaving.`);
        }
        if (savedDuplicates.length) {
            const noun = savedDuplicates.length === 1 ? 'study' : 'studies';
            messages.push(`Skipped ${savedDuplicates.length} ${noun} already saved to the cloud.`);
        }
        if (loadedDuplicates.length) {
            const noun = loadedDuplicates.length === 1 ? 'study' : 'studies';
            messages.push(`Skipped ${loadedDuplicates.length} duplicate ${noun} already loaded.`);
        }
        if (messages.length) {
            const variant = newStudies.length ? 'success' : 'warning';
            toast(messages.join(' '), variant);
        } else {
            toast('No new studies to load.', 'warning');
        }
    };

    const isSaveDisabled = saveStatus.state === 'saving' || isLoading || !studies.length;
    // Note: fileInputRef.current is intentionally NOT part of this check.
    // Refs are null on the first render and assigning them doesn't
    // trigger a re-render, so reading the ref here would leave the
    // button stuck `disabled` on a fresh mount (e.g. after navigating
    // in from /images). The handler does its own null-guard.
    const isUploadDisabled = isLoading || saveStatus.state === 'saving';

    // When Save flips from disabled to enabled (first study loaded),
    // apply a short-lived `attention` class that runs a pulse-glow +
    // bounce so the user notices the button is now actionable. If the
    // button becomes disabled again (e.g. saving started, or the list
    // was emptied) before the animation finishes, clear the flag so we
    // don't pulse a non-interactive button.
    useEffect(() => {
        const wasDisabled = prevSaveDisabledRef.current;
        prevSaveDisabledRef.current = isSaveDisabled;
        if (isSaveDisabled) {
            setSaveAttention(false);
            return;
        }
        if (!wasDisabled) return;
        setSaveAttention(true);
        const timeoutId = setTimeout(() => setSaveAttention(false), 4500);
        return () => clearTimeout(timeoutId);
    }, [isSaveDisabled]);

    return (
        <div className="review-list-container" style={{ paddingTop: '0px' }}>
            <ImagingHeader
                activeTab="upload"
                counts={user ? { images: savedStudiesCount, shares: sharesCount } : undefined}
                attentionTab={imagesTabAttention.active ? 'images' : null}
                attentionToken={imagesTabAttention.token}
                actions={(
                    <>
                        <button
                            type="button"
                            className={`imaging-action-button outline${saveAttention && !isSaveDisabled ? ' attention' : ''}`}
                            onClick={handleSave}
                            disabled={isSaveDisabled}
                        >
                            <Save size={20} strokeWidth={2} />
                            <span>{saveStatus.state === 'saving' ? 'Saving…' : (studies.length >= 2 ? 'Save All' : 'Save')}</span>
                        </button>
                        <button
                            type="button"
                            className="imaging-action-button primary"
                            onClick={handleSelectFolderClick}
                            disabled={isUploadDisabled}
                        >
                            <Upload size={20} strokeWidth={2} />
                            <span>{isLoading ? 'Loading...' : (studies.length >= 1 ? 'Upload Another' : 'Upload')}</span>
                        </button>
                    </>
                )}
            />

            <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                multiple
                webkitdirectory="true"
                directory="true"
                onChange={handleFilesChosen}
            />

            {progressState.visible && (
                <ProgressBar
                    style={{ Bottom: '20px' }}
                    label={progressState.label}
                    percent={progressState.percent}
                    detail={progressState.detail}
                    visible={!progressState.fading}
                />
            )}
            {studies.length === 0 && !isLoading ? (
                <ImagingEmptyState
                    actionLabel="Upload Images"
                    onAction={handleSelectFolderClick}
                />
            ) : studies.length > 0 ? (
                <>
                    <StudyListControls
                        search={listControls.search}
                        onSearchChange={listControls.setSearch}
                        tagOptions={listControls.tagOptions}
                        selectedTagKeys={listControls.selectedTagKeys}
                        onToggleTag={listControls.toggleTag}
                        onClearFilters={listControls.clearFilters}
                        hasActiveFilters={listControls.hasActiveFilters}
                        visibleCount={listControls.visibleStudies.length}
                        totalCount={studies.length}
                    />
                    {listControls.visibleStudies.length === 0 ? (
                        <StudyListNoResults onClearFilters={listControls.clearFilters} />
                    ) : (
                        <ImageCardList
                            ref={imageListRef}
                            studies={listControls.visibleStudies}
                            onSeriesClick={handleSeriesClick}
                            onTagsChange={handleLocalTagsChange}
                            onStudyRemoved={(uid) => {
                                setStudies((prev) => prev.filter((s) => s.studyInstanceUID !== uid));
                            }}
                        />
                    )}
                </>
            ) : null}

            <DicomViewer
                isOpen={isViewerOpen}
                onClose={() => setIsViewerOpen(false)}
                studies={studies}
                currentStudyUID={currentStudyUID}
                currentSeriesKey={currentSeriesKey}
                currentIndex={currentIndex}
                onStudyChange={handleViewerStudyChange}
                onSeriesChange={handleViewerSeriesChange}
                onIndexChange={setCurrentIndex}
            />
        </div>
    );
}

export default UploadImages;